import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import useSort from '@shared/hooks/useSort';
import { cleanParams } from '@shared/utils/cleanParams';
import { getDefaultDateRange } from '@shared/utils/formatDate';

/**
 * 결재 목록 공통 Hook
 *
 * - searchParams는 외부(부모)에서 관리
 * - 이 훅은 조회 + 가공 + UI 헬퍼 역할만 수행
 * - isActiveTab: 탭 전환 시 해당 탭이 활성화되면 필터/검색 초기화
 */
export default function useApprovalSection({
  queryType,
  searchParams, // ✅ 외부에서 주입
  setSearchParams, // ✅ 외부 setter 주입
  filterFieldBuilder,
  tableColumns,
  useQueryHook,
  formOptions,
  excelFormatter,
  defaultSort,
  isActiveTab = true, // 탭이 활성화되어 있는지 (탭 전환 시 초기화용)
}) {
  /**
   * 초기 날짜 범위 (UI 기본값 용도)
   */
  const defaultRange = getDefaultDateRange();

  /**
   * 화면에 바인딩되는 필터 입력값 상태 (UI 전용)
   */
  const [filterValues, setFilterValues] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  /** 탭 활성화 전환 감지용 (탭 전환 시 필터 초기화) */
  const prevActiveRef = useRef(false);

  /**
   * 탭 전환 시: 해당 탭이 활성화될 때 필터/검색 초기화
   */
  useEffect(
    function () {
      const prevWasActive = prevActiveRef.current;
      prevActiveRef.current = isActiveTab;

      if (!prevWasActive && isActiveTab) {
        const defaultFilterValues = {
          date1: defaultRange.start,
          date2: defaultRange.end,
          docType: '',
          name: '',
          team: '',
          stateCode: '',
          title: '',
          exeState: '',
        };
        setFilterValues(defaultFilterValues);
        setSearchParams({
          date1: defaultRange.start,
          date2: defaultRange.end,
        });
      }
    },
    [isActiveTab, defaultRange.start, defaultRange.end, setSearchParams],
  );

  /** 페이지네이션 상태 */
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  /**
   * React Query 호출
   * - searchParams 변경 시 자동 재조회
   */
  const {
    data: approvalList,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: approvalRefetch,
  } = useQueryHook(queryType, searchParams, {
    enabled: isActiveTab,
  });

  /**
   * 필터 UI 필드 구성
   */
  const filterFields = useMemo(
    () => filterFieldBuilder(filterValues, formOptions),
    [filterValues, formOptions],
  );

  /**
   * empty 판단
   */
  const isEmpty =
    !isLoading &&
    !isFetching &&
    (!approvalList ||
      approvalList.result_code === 110 ||
      approvalList?.resultData?.docInfoList?.length === 0);

  /** 테이블 원본 데이터 */
  const rawTableData =
    !approvalList || approvalList.result_code === 110
      ? []
      : (approvalList?.resultData?.docInfoList ?? []);

  /**
   * 중복 제거된 테이블 데이터
   * - API에서 중복된 데이터가 내려오는 경우를 방지하기 위해 idx 기준 필터링
   */
  const tableData = useMemo(() => {
    const seen = new Set();
    return rawTableData.filter((item) => {
      if (item.idx) {
        if (seen.has(item.idx)) return false;
        seen.add(item.idx);
      }
      return true;
    });
  }, [rawTableData]);

  /**
   * 정렬 처리
   */
  const { sortedData, sortField, sortDirection, handleSort } = useSort(tableData, defaultSort);

  /**
   * 페이지네이션 적용
   */
  const paginatedData = useMemo(() => {
    return sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  /**
   * 필터 입력 변경 (UI 상태만 변경)
   */
  const handleFilterChange = useCallback((key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * PC 검색 버튼용 핸들러
   * - filterValues → API searchParams 변환
   * - 최종 searchParams는 부모에서 관리
   */
  const handleSearch = useCallback(() => {
    const cleaned = cleanParams(filterValues);
    const apiParams = {};

    if (cleaned.date1) apiParams.date1 = cleaned.date1;
    if (cleaned.date2) apiParams.date2 = cleaned.date2;

    if (cleaned.docType !== undefined) {
      apiParams.formCode = cleaned.docType ? Number(cleaned.docType) : undefined;
    }

    if (cleaned.name?.trim()) {
      apiParams.name = cleaned.name.trim();
    }

    if (cleaned.team !== undefined) {
      apiParams.teamCode = cleaned.team ? Number(cleaned.team) : undefined;
    }

    if (cleaned.stateCode !== undefined) {
      apiParams.stateCode = cleaned.stateCode ? Number(cleaned.stateCode) : undefined;
    }

    if (cleaned.title?.trim()) {
      apiParams.title = cleaned.title.trim();
    }

    if (cleaned.exeState !== undefined) {
      apiParams.exeState = cleaned.exeState ? Number(cleaned.exeState) : undefined;
    }

    const finalParams = cleanParams(apiParams);

    setSearchParams(finalParams); // ✅ 부모 상태 변경
    setCurrentPage(1);
  }, [filterValues, setSearchParams]);

  /**
   * 필터 초기화
   */
  const handleReset = useCallback(() => {
    setFilterValues({
      date1: defaultRange.start,
      date2: defaultRange.end,
      docType: '',
      name: '',
      team: '',
      stateCode: '',
      title: '',
      exeState: '',
    });
  }, [defaultRange]);

  /**
   * Excel 다운로드
   */
  const handleExcel = useCallback(() => {
    return excelFormatter ? excelFormatter(sortedData) : sortedData;
  }, [sortedData, excelFormatter]);

  return {
    filterFields,
    tableColumns,
    paginatedData,
    tableData: sortedData,
    isEmpty,
    currentPage,
    totalPages,

    // UI 핸들러
    onFilterChange: handleFilterChange,
    onSearch: handleSearch,
    onReset: handleReset,
    onPageChange: setCurrentPage,

    // 정렬
    onSort: handleSort,
    sortField: { field: sortField, direction: sortDirection },

    // Excel
    handleExcel,

    // React Query 상태
    approvalRefetch,
    isLoading,
    isFetching,
    isError,
    error,

    setSearchParams,
  };
}
